import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import QuoteBuilder from "../components/ui/QuoteBuilder";
import ParallaxImage from "../components/ui/ParallaxImage";
import {
  ArrowRight,
  Palette,
  Eye,
  Layers,
  Flame,
  Mountain,
  Feather,
  BookOpen,
  MessageSquare,
  Calendar,
} from "lucide-react";
import { API } from "../apiConfig";

const MARKETING = {
  hero: "https://static.prod-images.emergentagent.com/jobs/d3d35e52-10c3-4c88-ba0b-938139dca4c0/images/4f777f497fa2c9f76843f0871d33e8a7990813773e574aca387f474d30d93a1e.png",
  studio: "https://static.prod-images.emergentagent.com/jobs/d3d35e52-10c3-4c88-ba0b-938139dca4c0/images/88f4006569e45db6951dc290e3632c624466e420adbd3728d9033a0efa47b97c.png",
  detail: "https://static.prod-images.emergentagent.com/jobs/d3d35e52-10c3-4c88-ba0b-938139dca4c0/images/1786da3f118da61d7bce802d1c5ed7a6761f0304468d5f2e4b0c41cc891be7de.png",
  interior: "https://static.prod-images.emergentagent.com/jobs/d3d35e52-10c3-4c88-ba0b-938139dca4c0/images/ff9060dfa972e64f0de9e36c77785cb7f785f1c84afee71d88a3dff615109dba.png",
};

const COLLECTION_THEMES = [
  {
    icon: Flame,
    title: "Dreamtime Stories",
    description:
      "Works that trace the ancient songlines and creation narratives passed down through generations.",
  },
  {
    icon: Mountain,
    title: "Country & Landscape",
    description:
      "Representations of the land as a living, spiritual entity — rivers, ridges, coastlines, and ceremonial grounds.",
  },
  {
    icon: Layers,
    title: "Cultural Preservation",
    description:
      "Pieces created to keep cultural knowledge alive. Colin's pyrography burns stories directly into timber.",
  },
  {
    icon: Feather,
    title: "Totemic Spirits",
    description:
      "The White Wallaby, the emu, the goanna — spirit animals that carry deep cultural significance.",
  },
  {
    icon: Eye,
    title: "Contemporary Witness",
    description:
      "Works addressing the intersection of ancient culture and modern Australia. Truth across time.",
  },
  {
    icon: Palette,
    title: "Technique & Craft",
    description:
      "A showcase of Colin's distinctive pyrography — each stroke irreversible, demanding absolute precision.",
  },
];

export default function TheCollectionPage() {
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, artRes] = await Promise.all([
          axios.get(`${API}/products`, { withCredentials: true }),
          axios.get(`${API}/articles?limit=4`),
        ]);
        let prods = prodRes.data;
        if (prods && !Array.isArray(prods)) prods = prods.products || [];
        prods = prods.filter((p) => p.is_public !== false);
        setProducts(prods);
        setArticles(Array.isArray(artRes.data) ? artRes.data : []);
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const featured = products.filter((p) => p.is_featured);
  const hero = featured[0] || products[0];
  const remaining = products.filter((p) => p.product_id !== hero?.product_id);

  return (
    <div className="min-h-screen bg-background" data-testid="the-collection-page">
      <Navigation />

      {/* ── Hero ── */}
      <section className="relative pt-36 pb-24 px-6 md:px-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[11px] tracking-[0.35em] uppercase text-primary mb-5">
              The Body of Work
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-6"
              data-testid="collection-hero-title"
            >
              The White Wallaby <em>Collection</em>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Over a lifetime of creation, Elder Colin Isaacs has built a body of
              work that is at once a personal artistic journey and a living
              cultural archive. Every piece carries 65,000 years of continuous
              heritage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── The Story ── */}
      <section className="py-20 px-6 md:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-primary mb-4">
              Origins
            </p>
            <h2 className="text-3xl md:text-4xl font-light mb-10">
              <em>The Story Behind the Collection</em>
            </h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Colin Isaacs — known as The White Wallaby — is a Gadigal and
                  Bidjigal Elder whose art practice spans decades of cultural
                  dedication. His work is not created for exhibition alone. It is
                  made to preserve, to teach, and to honour the oldest living
                  culture on Earth.
                </p>
                <p>
                  The White Wallaby Collection represents the totality of Colin's
                  creative output: original pyrography on timber, limited edition
                  fine art prints, and large-scale commissions placed in galleries,
                  corporate offices, and private residences worldwide.
                </p>
              </div>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Each artwork begins as a meditation. Colin works directly on raw
                  Australian hardwood, using heat and fire to render images that
                  tell Dreamtime stories, map ancestral journeys, and honour the
                  totemic spirits connecting people to Country.
                </p>
                <p>
                  The collection is deliberately finite. Every original is
                  one-of-one, and every print edition is strictly limited and
                  numbered. Once an edition sells out, it is never reprinted. This
                  is art that appreciates — in value, in meaning, and in cultural
                  significance.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Themes ── */}
      <section className="py-20 px-6 md:px-16 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-primary mb-3">
              Themes & Narratives
            </p>
            <h2 className="text-2xl md:text-3xl font-light mb-10">
              <em>What the Collection Explores</em>
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COLLECTION_THEMES.map((theme, i) => {
              const Icon = theme.icon;
              return (
                <motion.div
                  key={theme.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <Card
                    className="h-full rounded-2xl border-border/60 hover:border-primary/30 transition-colors"
                    data-testid={`theme-card-${i}`}
                  >
                    <CardContent className="pt-6 space-y-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-medium text-base">{theme.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {theme.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Provenance Stats ── */}
      <section className="py-16 px-6 md:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            <div className="text-center p-6">
              <p className="text-4xl font-light text-primary mb-2">65,000+</p>
              <p className="text-sm text-muted-foreground">
                Years of continuous cultural heritage encoded in every work
              </p>
            </div>
            <div className="text-center p-6">
              <p className="text-4xl font-light text-primary mb-2">
                One of One
              </p>
              <p className="text-sm text-muted-foreground">
                Every original pyrography is unique and irreproducible
              </p>
            </div>
            <div className="text-center p-6">
              <p className="text-4xl font-light text-primary mb-2">
                Never Reprinted
              </p>
              <p className="text-sm text-muted-foreground">
                Limited editions are strictly numbered and permanently closed
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          THE GALLERY — artwork grid integrated here
          ═══════════════════════════════════════════════════ */}

      {/* Featured Work — editorial spread */}
      {hero && (
        <section className="py-24 px-8 md:px-16 border-t border-border">
          <div className="max-w-7xl mx-auto">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-12"
            >
              The Gallery
            </motion.p>
            <Link
              to={`/products/${hero.product_id}`}
              className="group block"
              data-testid="hero-artwork"
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid md:grid-cols-12 gap-8 md:gap-16 items-center"
              >
                <div className="md:col-span-7 overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={hero.images?.[0] || ""}
                      alt={hero.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]"
                    />
                  </div>
                </div>
                <div className="md:col-span-5">
                  <p className="plate-no text-primary mb-3">Plate N°01</p>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                    Featured Work
                  </p>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-5">
                    <em>{hero.title}</em>
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6 line-clamp-3">
                    {hero.story || hero.description}
                  </p>
                  <span className="inline-flex items-center text-sm font-medium text-primary border-b border-primary/40 pb-1 group-hover:border-primary transition-colors">
                    Explore This Work
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </motion.div>
            </Link>
          </div>
        </section>
      )}

      {/* Asymmetric Gallery Grid */}
      {remaining.length > 0 && (
        <section className="py-12 px-8 md:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-12 gap-6 md:gap-8">
              {remaining.map((product, i) => {
                const pattern = i % 4;
                const colClass =
                  pattern === 0
                    ? "col-span-12 md:col-span-7"
                    : pattern === 1
                    ? "col-span-12 md:col-span-5"
                    : pattern === 2
                    ? "col-span-12 md:col-span-5"
                    : "col-span-12 md:col-span-7";
                const aspectClass =
                  pattern === 0
                    ? "aspect-[4/3]"
                    : pattern === 1
                    ? "aspect-[3/4]"
                    : pattern === 2
                    ? "aspect-[4/3]"
                    : "aspect-[3/4]";

                return (
                  <motion.div
                    key={product.product_id}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: (i % 2) * 0.12 }}
                    className={`${colClass} ${pattern === 1 ? "md:mt-12" : ""}`}
                  >
                    <Link
                      to={`/products/${product.product_id}`}
                      className="group block"
                      data-testid={`product-card-${product.product_id}`}
                    >
                      <div className={`${aspectClass} overflow-hidden mb-5`}>
                        <img
                          src={product.images?.[0] || ""}
                          alt={product.title}
                          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                          loading="lazy"
                        />
                      </div>
                      <div className="max-w-md">
                        <p className="plate-no text-primary/80 mb-2">
                          Plate N°{String(i + 2).padStart(2, "0")}
                        </p>
                        <p className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                          {product.medium ||
                            product.category?.replace("_", " ") ||
                            "Giclée Reproduction"}
                        </p>
                        <h3 className="text-xl md:text-2xl font-light group-hover:text-primary transition-colors duration-300">
                          <em>{product.title}</em>
                        </h3>
                        {product.story && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 leading-relaxed">
                            {product.story}
                          </p>
                        )}
                        <span className="inline-flex items-center text-sm mt-3 text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          View Work
                          <ArrowRight className="h-3.5 w-3.5 ml-1.5 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════
          ARTICLES — "Our Stories"
          ═══════════════════════════════════════════════════ */}
      {articles.length > 0 && (
        <section className="py-24 px-8 md:px-16 bg-muted/30 border-t border-border">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-14"
            >
              <p className="text-[11px] tracking-[0.3em] uppercase text-primary mb-3">
                Journal
              </p>
              <h2 className="text-3xl md:text-4xl font-light mb-4">
                <em>Our Stories</em>
              </h2>
              <p className="text-muted-foreground max-w-xl">
                Insights into Aboriginal art, collecting, and the cultural
                significance behind every work.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-10">
              {articles.map((article, i) => (
                <motion.div
                  key={article._id || article.slug || i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <Link
                    to={`/articles/${article.slug}`}
                    className="group block"
                    data-testid={`article-card-${i}`}
                  >
                    {/* Article image */}
                    {article.featured_image && (
                      <div className="aspect-[16/9] overflow-hidden mb-5 rounded-xl">
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                      </div>
                    )}
                    {/* Category + Date */}
                    <div className="flex items-center gap-3 mb-3">
                      {article.category && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-primary font-medium">
                          {article.category}
                        </span>
                      )}
                      {article.published_at && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(article.published_at).toLocaleDateString(
                            "en-AU",
                            { month: "short", day: "numeric", year: "numeric" }
                          )}
                        </span>
                      )}
                    </div>
                    {/* Title */}
                    <h3 className="text-xl font-light group-hover:text-primary transition-colors duration-300 mb-2 leading-snug">
                      <em>{article.title}</em>
                    </h3>
                    {/* Excerpt */}
                    {article.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                    )}
                    <span className="inline-flex items-center text-sm mt-3 text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Read More
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════
          MARKETING IMAGES — at the bottom
          ═══════════════════════════════════════════════════ */}

      {/* Lifestyle interior image */}
      <ParallaxImage
        src={MARKETING.interior}
        alt="Aboriginal art in luxury interior"
        className="h-[70vh] min-h-[420px]"
      >
        <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center px-6"
          >
            <p className="text-[11px] tracking-[0.35em] uppercase text-white/50 mb-4">
              Transform Your Space
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-light text-white italic">
              Where Art Lives
            </h2>
          </motion.div>
        </div>
      </ParallaxImage>

      {/* Studio + Commission CTA */}
      <section className="border-t border-border">
        <div className="grid md:grid-cols-2">
          <div className="h-[50vh] md:h-auto overflow-hidden">
            <img
              src={MARKETING.studio}
              alt="Colin Isaacs in studio"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex items-center px-8 md:px-16 py-16 md:py-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-lg"
            >
              <p className="text-[11px] tracking-[0.3em] uppercase text-primary mb-4">
                Direct from the Artist
              </p>
              <h2 className="text-3xl md:text-4xl font-light mb-5 leading-tight">
                Commission a <em>Bespoke</em> Piece
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Every space is unique. Tell us about yours and we'll help you
                find — or create — the perfect artwork. Choose your substrate,
                size, and framing for a truly personal result.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="rounded-full px-10 h-12"
                  onClick={() => setQuoteOpen(true)}
                  data-testid="commission-cta"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Start Your Quote
                </Button>
                <Link to="/commissions">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 h-12"
                  >
                    Our Services
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery + Detail image strip */}
      <section className="grid md:grid-cols-2 h-[50vh] min-h-[320px]">
        <ParallaxImage src={MARKETING.hero} alt="Arawol gallery space" className="h-full" speed={8} />
        <ParallaxImage src={MARKETING.detail} alt="Aboriginal dot painting detail" className="h-full" speed={8} />
      </section>

      <Footer />
      <QuoteBuilder open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
